import { logger } from '@/utils/logger';
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
    interface Window {
      google: any;
    }
}

interface GoogleOAuthOptions {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  access_type: string;
  prompt: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string;
}

// PKCE 유틸리티 함수들
function base64url(uint8: Uint8Array): string {
  return btoa(String.fromCharCode(...Array.from(uint8)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomVerifier(length: number = 64): string {
  const buf = new Uint8Array(length);
  crypto.getRandomValues(buf);
  return base64url(buf).slice(0, length);
}

// SHA-256 해시 함수 (HTTPS 환경에서만 동작)
async function sha256(input: string): Promise<Uint8Array> {
  const enc = new TextEncoder().encode(input);
  // 문제: crypto.subtle은 HTTPS에서만 동작
  // 개선: HTTP 환경 감지 및 대체 방안
  if (!window.crypto?.subtle) {
    // HTTPS가 아닌 환경에서는 SHA-256을 지원하지 않음
    throw new Error('SHA-256은 HTTPS 환경에서만 사용 가능합니다. HTTPS를 사용해주세요.');
  }
  const digest = await crypto.subtle.digest("SHA-256", enc as BufferSource);
  return new Uint8Array(digest);
}

async function toCodeChallenge(verifier: string): Promise<string> {
  return base64url(await sha256(verifier));
}

// Google OAuth 초기화
export const initGoogleAuth = () => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const timeout = 10000; // 10 second timeout

    const interval = setInterval(() => {
      if (window.google) {
        clearInterval(interval);
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: () => {}, // 콜백은 별도로 처리
        });
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        // Timeout after 10 seconds
        clearInterval(interval);
        logger.error('Google SDK initialization timeout', { timeout });
        reject(new Error('Google SDK failed to load within timeout'));
      }
    }, 100);
  });
};

/// googleAuth.ts 수정 - 팝업이 닫히지 않도록 임시 수정
export const getGoogleAuthUrl = async (): Promise<string> => {
  const rootUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
  const redirect_uri = `${window.location.origin}/auth/google/callback`;
  
  const code_verifier = randomVerifier(64);
  const codeChallenge = await toCodeChallenge(code_verifier);
  
  const PKCE_VERIFIER_KEY = 'google_pkce_verifier';
  sessionStorage.setItem(PKCE_VERIFIER_KEY, code_verifier);
  
  const options: GoogleOAuthOptions = {
    client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    redirect_uri: redirect_uri,
    response_type: 'code',
    scope: [
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/userinfo.email',
    ].join(' '),
    access_type: 'offline',
    prompt: 'consent',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: 'state_parameter_passthrough_value',
  };

  const qs = new URLSearchParams(options as unknown as Record<string, string>);
  return `${rootUrl}?${qs.toString()}`;
};

// 구글 계정 연결 해제
export const revokeGoogleAccess = async (accessToken?: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // 방법 1: 액세스 토큰으로 revoke (TODO: 백엔드에서 처리하는 것을 권장)
    if (accessToken) {
      const response = await fetch(`https://oauth2.googleapis.com/revoke?token=${accessToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.ok) {
        return { success: true };
      } else {
        logger.error('구글 토큰 revoke 실패:', { status: response.status });
        // 이미 revoke된 토큰이거나 만료된 경우에도 성공으로 처리
        if (response.status === 400) {
          return { success: true };
        }
        return { success: false, error: 'Failed to revoke Google access' };
      }
    }

    // 방법 2: Google Identity Services 사용 (클라이언트 측)
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }

    return { success: true };
  } catch (error) {
    logger.error('구글 연결 해제 오류:', { error });
    return { success: false, error: '구글 연결 해제 중 오류가 발생했습니다.' };
  }
};